import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import _ from "lodash";
import { canonicalQueryCacheKey } from "permissions/_api/queries/canonical-query-cache-key";
import { PermissionFilterParametersProps } from "permissions/_api/queries/filtering/permission-filter-schema";

export default class PermissionFilterParameters {
  public readonly hasParent?: boolean;
  public readonly resource?: unknown;
  public readonly parentResource?: unknown;
  public readonly parentId?: Guid;
  public readonly legalEntityId?: Guid;
  public readonly granteeUserIds?: Guid[];
  public readonly grantorUserId?: Guid;
  public readonly maxCreatedDate?: AHDate;
  public readonly minCreatedDate?: AHDate;
  public readonly showDisabled?: boolean;
  public readonly minDisabledDate?: AHDate;
  public readonly maxDisabledDate?: AHDate;
  public readonly categories?: string[];
  public readonly accessType?: string;
  public readonly documentIds?: Guid[];
  public readonly forumIds?: Guid[];
  public readonly proposalIds?: Guid[];
  public readonly workAgreementIds?: Guid[];
  public readonly name?: string;

  public get searchParams(): URLSearchParams {
    const searchParams = new URLSearchParams();

    if (this.hasParent !== undefined) searchParams.append("hasParent", String(this.hasParent));
    if (this.resource) {
      const resourceString = typeof this.resource === "string" ? this.resource : JSON.stringify(this.resource);
      searchParams.append("resource", resourceString);
    }
    if (this.parentResource) {
      const parentResourceString =
        typeof this.parentResource === "string" ? this.parentResource : JSON.stringify(this.parentResource);
      searchParams.append("parentResource", parentResourceString);
    }
    if (this.parentId) searchParams.append("parentId", this.parentId.value);
    if (this.legalEntityId) searchParams.append("legalEntityId", this.legalEntityId.value);

    if (this.granteeUserIds?.length) {
      for (const id of this.granteeUserIds) {
        searchParams.append("granteeUserIds", id.value);
      }
    }

    if (this.grantorUserId) searchParams.append("grantorUserId", this.grantorUserId.value);
    if (this.maxCreatedDate) searchParams.append("maxCreatedDate", this.maxCreatedDate.toISOString());
    if (this.minCreatedDate) searchParams.append("minCreatedDate", this.minCreatedDate.toISOString());
    if (this.showDisabled !== undefined) searchParams.append("showDisabled", String(this.showDisabled));
    if (this.maxDisabledDate) searchParams.append("maxDisabledDate", this.maxDisabledDate.toISOString());
    if (this.minDisabledDate) searchParams.append("minDisabledDate", this.minDisabledDate.toISOString());

    if (this.categories?.length) {
      for (const c of this.categories) {
        searchParams.append("categories", c);
      }
    }

    if (this.accessType) searchParams.append("accessType", this.accessType);
    if (this.documentIds?.length) {
      for (const id of this.documentIds) {
        searchParams.append("documentIds", id.value);
      }
    }
    if (this.forumIds?.length) {
      for (const id of this.forumIds) {
        searchParams.append("forumIds", id.value);
      }
    }
    if (this.proposalIds?.length) {
      for (const id of this.proposalIds) {
        searchParams.append("proposalIds", id.value);
      }
    }
    if (this.workAgreementIds?.length) {
      for (const id of this.workAgreementIds) {
        searchParams.append("workAgreementIds", id.value);
      }
    }
    if (this.name) searchParams.append("name", this.name);

    return searchParams;
  }

  public get cacheKey(): string {
    return canonicalQueryCacheKey(this.searchParams);
  }

  public constructor(filters: PermissionFilterParametersProps) {
    this.hasParent = filters.hasParent;
    this.resource = filters.resource;
    this.parentResource = filters.parentResource;
    this.parentId = filters.parentId;
    this.legalEntityId = filters.legalEntityId;
    this.granteeUserIds = filters.granteeUserIds;
    this.grantorUserId = filters.grantorUserId;
    this.maxCreatedDate = filters.maxCreatedDate;
    this.minCreatedDate = filters.minCreatedDate;
    this.showDisabled = filters.showDisabled;
    this.minDisabledDate = filters.minDisabledDate;
    this.maxDisabledDate = filters.maxDisabledDate;
    this.categories = filters.categories;
    this.accessType = filters.accessType;
    this.documentIds = filters.documentIds;
    this.forumIds = filters.forumIds;
    this.proposalIds = filters.proposalIds;
    this.workAgreementIds = filters.workAgreementIds;
    this.name = filters.name;
  }

  public isEqualTo(other: PermissionFilterParameters | undefined): boolean {
    if (!other) return false;

    const guidSetsEqual = (left: Guid[] | undefined, right: Guid[] | undefined) =>
      (!left && !right) ||
      (!!left && !!right && left.length === right.length && left.every((id) => right.some((otherId) => otherId.isEqualTo(id))));

    const categoriesEqual =
      (!this.categories && !other.categories) ||
      (!!this.categories &&
        !!other.categories &&
        this.categories.length === other.categories.length &&
        this.categories.every((c) => other.categories!.includes(c)));

    return (
      this.hasParent === other.hasParent &&
      _.isEqual(this.resource, other.resource) &&
      _.isEqual(this.parentResource, other.parentResource) &&
      (!!this.parentId === !!other.parentId ? this.parentId?.isEqualTo(other.parentId) ?? true : false) &&
      (!!this.legalEntityId === !!other.legalEntityId
        ? this.legalEntityId?.isEqualTo(other.legalEntityId) ?? true
        : false) &&
      guidSetsEqual(this.granteeUserIds, other.granteeUserIds) &&
      (!!this.grantorUserId === !!other.grantorUserId
        ? this.grantorUserId?.isEqualTo(other.grantorUserId) ?? true
        : false) &&
      (!!this.maxCreatedDate === !!other.maxCreatedDate
        ? this.maxCreatedDate?.isEqualTo(other.maxCreatedDate) ?? true
        : false) &&
      (!!this.minCreatedDate === !!other.minCreatedDate
        ? this.minCreatedDate?.isEqualTo(other.minCreatedDate) ?? true
        : false) &&
      this.showDisabled === other.showDisabled &&
      (!!this.maxDisabledDate === !!other.maxDisabledDate
        ? this.maxDisabledDate?.isEqualTo(other.maxDisabledDate) ?? true
        : false) &&
      (!!this.minDisabledDate === !!other.minDisabledDate
        ? this.minDisabledDate?.isEqualTo(other.minDisabledDate) ?? true
        : false) &&
      categoriesEqual &&
      this.accessType === other.accessType &&
      guidSetsEqual(this.documentIds, other.documentIds) &&
      guidSetsEqual(this.forumIds, other.forumIds) &&
      guidSetsEqual(this.proposalIds, other.proposalIds) &&
      guidSetsEqual(this.workAgreementIds, other.workAgreementIds) &&
      this.name === other.name
    );
  }

  public toString(): string {
    return this.searchParams.toString();
  }
}
